import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { ShoppingCart, ArrowLeft, Plus, Minus, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getProductImageUrl } from '../utils/imageUtils';
import { formatSellingUnit, formatUnitConversion } from './admin-products/catalogUnits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setQuantity(response.data.min_order_quantity || 1);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart({
        id: product._id,
        name: product.name,
        price: product.price,
        unit: product.unit,
        unit_value: product.unit_value || 1,
        min_order_quantity: product.min_order_quantity || 1,
        image: getProductImageUrl(product)
      }, quantity);
      toast.success(`${quantity} x ${product.name} added to cart!`);
    }
  };

  const incrementQuantity = () => {
    if (product && (product.availability === 'in_stock' || (!product.availability && quantity < product.stock))) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > (product?.min_order_quantity || 1)) {
      setQuantity(quantity - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <Link to="/shop">
            <Button>Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isAvailable = product.availability
    ? product.availability === 'in_stock'
    : Number(product.stock) > 0;
  const minOrder = product.min_order_quantity || 1;
  const isTonneProduct = product.unit === 'tonne';
  const quoteQuery = new URLSearchParams({
    product: product.name,
    productId: product._id,
    quantity: `${minOrder} tonne${minOrder > 1 ? 's' : ''}`,
    source: 'tonne-product',
  }).toString();

  return (
    <div className="product-detail-page py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link to="/shop">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Shop
          </Button>
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="h-96 md:h-[600px] rounded-2xl overflow-hidden shadow-xl">
              <img
                src={getProductImageUrl(product)}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/600x600?text=No+Image'; }}
              />
            </div>
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {product.name}
            </h1>
            
            <div className="flex items-baseline gap-4 mb-6">
              <span className="text-4xl font-bold text-green-600">
                ₦{product.price.toLocaleString()}
              </span>
              <span className="text-lg text-gray-600" data-testid="product-detail-unit-price">
                per {formatSellingUnit(product.unit_value, product.unit)}
              </span>
            </div>

            <div className="mb-6">
              <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                isAvailable
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`} data-testid="product-detail-availability">
                {isAvailable ? 'Available' : 'Out of stock'}
              </span>
            </div>

            <div className="prose prose-lg mb-8">
              <p className="text-gray-700 leading-relaxed">
                {product.description}
              </p>
            </div>

            <Card className="mb-6 border-2 border-green-100">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4">Product Details</h3>
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between">
                    <span>Category:</span>
                    <span className="font-semibold capitalize">
                      {(product.category || '').replace(/[_-]/g, ' ')}
                    </span>
                  </div>
                  {product.subcategory && (
                    <div className="flex justify-between">
                      <span>Type:</span>
                      <span className="font-semibold capitalize">
                        {product.subcategory.replace(/[_-]/g, ' ')}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Sale unit:</span>
                    <span className="font-semibold">{formatSellingUnit(product.unit_value, product.unit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Conversion:</span>
                    <span className="font-semibold" data-testid="product-detail-conversion">{formatUnitConversion(product.unit_value, product.unit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>MOQ:</span>
                    <span className="font-semibold" data-testid="product-detail-moq">{minOrder} sale unit{minOrder > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity (sale units)
              </label>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={decrementQuantity}
                  disabled={quantity <= minOrder}
                  data-testid="product-detail-decrease-button"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-2xl font-bold w-16 text-center" data-testid="product-detail-quantity">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={incrementQuantity}
                  disabled={!isAvailable || (!product.availability && quantity >= product.stock)}
                  data-testid="product-detail-increase-button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <Button
              onClick={handleAddToCart}
              disabled={!isAvailable}
              size="lg"
              className="w-full mb-4"
              data-testid="product-detail-add-to-cart"
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              Add to Cart - ₦{(product.price * quantity).toLocaleString()}
            </Button>

            <Button
              onClick={() => {
                handleAddToCart();
                navigate('/cart');
              }}
              disabled={!isAvailable}
              size="lg"
              variant="outline"
              className="w-full"
              data-testid="product-detail-buy-now"
            >
              Buy Now
            </Button>

            {isTonneProduct && (
              <Link to={`/wholesale?${quoteQuery}`} className="block" data-testid="product-detail-request-quote-link">
                <Button size="lg" variant="outline" className="mt-4 w-full border-amber-500 text-amber-800 hover:bg-amber-50" data-testid="product-detail-request-quote-button">
                  <FileText className="mr-2 h-5 w-5" /> Request a wholesale quote
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;